import * as React from 'react';
import { cloneDeep, flattenDeep } from 'lodash';
import { DiffNodeComp } from './diff-node-comp';
import { DiffNodeConfig, DiffNodeRenderType, DiffFieldConfig } from './node-config';
import { isEqual } from '@lib/index';
import { localizedString } from '@locale/index';

function getFieldData(data: any, fieldConfig: DiffFieldConfig) {
  return data[fieldConfig.key];
}

function nodeEqual(obj1: any, obj2: any, node?: DiffNodeConfig) {

  if (obj1 === obj2) {
    return true;
  }

  if (!obj1 || !obj2) {
    return false;
  }

  if (!node) {
    return isEqual(obj1, obj2);
  }

  let ret = true;

  for (const field of node.fields) {
    const v1 = getFieldData(obj1, field);
    const v2 = getFieldData(obj2, field);

    if (!v1 && !v2) {
      continue;
    }

    if (!v1 || !v2) {
      ret = false;
      break;
    }

    if (field.isArray) {

      if (v1.length !== v2.length) {
        ret = false;
        break;
      }

      for (let i = 0; i < v1.length; i += 1) {
        const item1 = v1[i];
        const item2 = v2[i];

        const eq = field.node
          ? nodeEqual(item1, item2, field.node)
          : isEqual(item1, item2);
        if (!eq) {
          ret = false;
          break;
        }
      }
    } else {

      const eq = field.node
        ? nodeEqual(v1, v2, field.node)
        : isEqual(v1, v2);
      if (!eq) {
        ret = false;
        break;
      }
    }

    if (!ret) {
      break;
    }
  }

  return ret;
}

export interface DiffCompProps {
  basePath: string[];
  rootNode?: DiffNodeConfig;
  masterData?: any;
  localData?: any;
  manageDiff?: boolean;
}

export interface DiffCompState { }

export class DiffComp extends React.PureComponent<
  DiffCompProps,
  DiffCompState
> {

  renderNode = ({
    masterData,
    localData,
    node,
    basePath = [],
    onResolve
  }: {
    masterData?: any;
    localData?: any;
    node: DiffNodeConfig;
    basePath?: string[];
    onResolve?: (data: any) => void;
  }): React.ReactNode => {

    if (!masterData && !localData) {
      return '';
    }

    if (!masterData || !localData) {
      return this.getDiffComp({
        masterData,
        localData,
        node,
        path: basePath
      });
    }

    return flattenDeep(node.fields
      .filter((field: DiffFieldConfig) => {
        let tmpMaster = masterData && getFieldData(masterData, field);
        let tmpLocal = localData && getFieldData(localData, field);

        let diff = false;

        if (field.isArray) {
          if ((tmpMaster || []).length !== (tmpLocal || []).length) {
            return true;
          }

          tmpMaster = tmpMaster || [];
          tmpLocal = tmpLocal || [];

          if (!diff) {
            for (let i = 0; i < Math.max(tmpMaster.length, tmpLocal.length); i += 1) {
              const masterItem = tmpMaster[i];
              const localItem = tmpLocal[i];
              const same = field.node
                ? nodeEqual(masterItem, localItem, field.node)
                : isEqual(masterItem, localItem);

              if (!same) {
                diff = true;
                break;
              }
            }
          }
        } else {
          tmpMaster = tmpMaster || {};
          tmpLocal = tmpLocal || {};

          const same = (tmpMaster || tmpLocal) &&
            (field.node
              ? nodeEqual(tmpMaster, tmpLocal, field.node)
              : isEqual(tmpMaster, tmpLocal));

          if (!same) {
            diff = true;
          }
        }

        return diff;
      })
      .map((field: DiffFieldConfig) => {
        const tmpMaster = masterData && getFieldData(masterData, field);
        const tmpLocal = localData && getFieldData(localData, field);

        const tmpPath = cloneDeep(basePath || []);
        if (field.localeNameKey) {
          tmpPath.push(localizedString(field.localeNameKey));
        } else {
          tmpPath.push(field.name || field.key);
        }

        if (field.node) {
          if (field.isArray) {
            const renderArr = [];

            if (field.node.idKey) {
              const idKey = field.node.idKey;

              for (const [index, masterItem] of tmpMaster.entries()) {

                const localItem = tmpLocal.find((it: any) => isEqual(it[idKey], masterItem[idKey]));
                if (localItem) {
                  tmpLocal.splice(localItem);

                  const nextPath = cloneDeep(tmpPath);
                  nextPath.push(
                    field.node
                      ? field.node.nameKey
                        ? localItem[field.node.nameKey]
                        : localItem.name
                      : `[${index}]`
                  );

                  if (!nodeEqual(localItem, masterItem, field.node)) {

                    const renderItem = this.renderNode({
                      node: field.node,
                      masterData: masterItem,
                      localData: localItem,
                      basePath: nextPath
                    });
                    renderArr.push(renderItem);
                  }
                } else {

                  const nextPath = cloneDeep(tmpPath);
                  nextPath.push(
                    field.node
                      ? field.node.nameKey
                        ? masterItem[field.node.nameKey]
                        : masterItem.name
                      : `[${index}]`
                  );

                  const renderItem = this.getDiffComp({
                    node: field.node,
                    masterData: masterItem,
                    renderType: field.renderType,
                    path: nextPath
                  });

                  renderArr.push(renderItem);
                }
              }

              for (const [index, localItem] of tmpLocal.entries()) {
                const nextPath = cloneDeep(tmpPath);
                nextPath.push(
                  field.node
                    ? field.node.nameKey
                      ? localItem[field.node.nameKey]
                      : localItem.name
                    : `[${index}]`
                );

                const renderItem = this.getDiffComp({
                  node: field.node,
                  localData: localItem,
                  renderType: field.renderType,
                  path: nextPath
                });

                renderArr.push(renderItem);
              }
              return renderArr;

            }

            return this.getDiffComp({
              isArray: field.isArray,
              node: field.node,
              renderType: field.renderType,
              masterData: tmpMaster,
              localData: tmpLocal,
              path: [
                ...(basePath || []),
                (field.localeNameKey
                  ? localizedString(field.localeNameKey)
                  : field.name || '')
              ]
            });

          }

          return this.renderNode({
            masterData: tmpMaster,
            localData: tmpLocal,
            node: field.node,
            basePath: [
              ...(basePath || []),
              field.localeNameKey
                ? localizedString(field.localeNameKey)
                : field.name]
          });
        }

        return this.getDiffComp({
          isArray: field.isArray,
          masterData: tmpMaster,
          localData: tmpLocal,
          renderType: field.renderType,
          path: [
            ...(basePath || []),
            (field.localeNameKey
              ? localizedString(field.localeNameKey)
              : field.name || '')
          ]
        });

      }));
  };

  render() {
    const {
      masterData,
      localData,
      rootNode,
      basePath
    } = this.props;

    return (
      <div className="diff-comp">
        {
          !rootNode && this.getDiffComp({
            masterData,
            localData,
            path: basePath
          })
        }
        {
          rootNode && this.renderNode({
            masterData,
            localData,
            basePath,
            node: rootNode
          })
        }
      </div>
    );
  }

  getDiffComp = ({
    path = [],
    isArray,
    masterData,
    localData,
    node,
    renderType
  }: {
    path?: string[];
    isArray?: boolean;
    masterData?: any;
    localData?: any;
    node?: DiffNodeConfig;
    renderType?: DiffNodeRenderType;
  }) => {
    return (
      <DiffNodeComp
        key={path.join('/')}
        path={path}
        masterData={masterData}
        localData={localData}
        node={node}
        renderType={renderType}
        isArray={isArray}
      />
    );
  }
}
